import { Component, Input, Output, EventEmitter, forwardRef, inject, signal, AfterViewInit, OnInit, computed, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatRadioModule } from '@angular/material/radio';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatExpansionModule } from '@angular/material/expansion';
import { SmartConsentOptionsComponent } from '../smart-consent-options/smart-consent-options.component';
import { AddCheckboxConsentComponent } from '../add-checkbox-consent.component';
import { SmartMeterAgreementComponent, SmartMeterAgreementData } from '../smart-meter-agreement/smart-meter-agreement.component';
import { UtilService } from '@services/util.service';
import { LoginService, UserDetails } from '@services/login.service';
import { RelationshipType, Consent, ConsentType, CheckboxConsent, StripeConsent, ConsentCategory, SmartConsentValue } from '@models/consent.model';
import { MeterDetails } from '@models/meter.model';



// Strict value type emitted by SmartConsentOptionsComponent.
// Ensures no 'any' usage and no fallback inference required.
// Each variant pairs a specific consentType with its consentData shape.

@Component({
  selector: 'app-add-smart-consent',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatCheckboxModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatRadioModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatProgressBarModule,
    MatExpansionModule,
    SmartConsentOptionsComponent,
    AddCheckboxConsentComponent,
    SmartMeterAgreementComponent
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AddSmartConsentComponent),
      multi: true
    }
  ],
  templateUrl: './add-smart-consent.component.html',
  styleUrl: './add-smart-consent.component.scss'
})
export class AddSmartConsentComponent implements ControlValueAccessor, OnInit, AfterViewInit {
  @Input() relationshipType: RelationshipType = RelationshipType.OCCUPIER;
  @Input() selectedMeters: MeterDetails[] = [];
  @Input() supportedSmartMeterConsentTypes: ConsentType[] = [
    ConsentType.MAC_ADDRESS,
    ConsentType.BILL_UPLOAD,
    ConsentType.STRIPE
  ];
  
  @Output() triggerAutoScroll = new EventEmitter<void>();
  
  // Inline option child components are now encapsulated in SmartConsentOptionsComponent
  
  private loginService = inject(LoginService);
  private utilService = inject(UtilService);
  
  // Internal value - now standardized to always be a Consent object
  private _value: Consent | null = null;
  
  // Control value accessor methods - standardized to work with Consent type
  private _onChange = (value: Consent | null) => {};
  private _onTouched = () => {};
  
  // Loading states
  processingSmartConsent = signal(false);
  smartConsentComplete = signal(false);
  processingStripeVerification = signal(false);
  stripeVerificationComplete = signal(false);
  
  // Smart meter terms link
  smartMeterTermsLink = signal<string | null>(null);
  loadingTermsLink = signal(false);
  
  // MAC address guide expanded state
  macGuideExpanded = signal(false);
  
  // Reactive signals for form validation
  scopeValue = signal<'ANNUAL' | 'HH' | null>(null);
  isMoveInDateValid = signal(false);
  isTermsAccepted = signal(false);
  
  // Computed signal for whether consent methods can be shown
  canShowConsentMethods = computed(() => {
    return this.scopeValue() === 'HH' && 
           this.isMoveInDateValid() && 
           this.isTermsAccepted();
  });

  // New control captures fully structured SmartConsentValue
  consentDataControl = new FormControl<SmartConsentValue | null>(null);
  
  // Maximum date for move-in date picker (today)
  maxDate = new Date();
  
  // Scope control (formerly granularity)
  scopeControl = new FormControl<'ANNUAL' | 'HH' | null>(null, [Validators.required]);

  // Annual checkbox consent control
  annualCheckboxConsentControl = new FormControl<CheckboxConsent | null>(null);

  // Smart meter agreement control (replaces individual move-in date and terms controls)
  smartMeterAgreementControl = new FormControl<SmartMeterAgreementData | null>(null);

  // Legacy controls (kept for backward compatibility)
  moveInDateControl = new FormControl<Date | null>(null, [Validators.required]);
  smartMeterTermsControl = new FormControl(false, [Validators.requiredTrue]);
  
  // Get logged in user details
  get loggedInUser(): UserDetails | null {
    return this.loginService.loginUser;
  }

  ngOnInit(): void {
    // Subscribe to scope changes
    this.scopeControl.valueChanges.subscribe(value => {
      // Update reactive signal for scope value
      this.scopeValue.set(value);
      this.onScopeValueChange(value);
    });

    // Subscribe to annual checkbox consent changes
    this.annualCheckboxConsentControl.valueChanges.subscribe(checkboxConsent => {
      if (this.scopeControl.value === 'ANNUAL' && checkboxConsent) {
        const consent = this.createAnnualConsentFromCheckbox(checkboxConsent);
        this._value = consent;
        this.smartConsentComplete.set(true);
        this._onChange(this._value);
        this._onTouched();
      } else if (this.scopeControl.value === 'ANNUAL') {
        this._value = null;
        this.smartConsentComplete.set(false);
        this._onChange(null);
      }
    });

    // Subscribe to smart meter agreement changes (new approach)
    this.smartMeterAgreementControl.valueChanges.subscribe(agreementData => {
      if (this.scopeControl.value === 'HH') {
        // Update reactive signals based on agreement data
        this.isMoveInDateValid.set(!!(agreementData?.moveInDate));
        this.isTermsAccepted.set(!!(agreementData?.termsAccepted));
        
        // Sync legacy controls for backward compatibility
        if (agreementData) {
          this.moveInDateControl.setValue(agreementData.moveInDate, { emitEvent: false });
          this.smartMeterTermsControl.setValue(agreementData.termsAccepted, { emitEvent: false });
        }
        
        this.updateHalfHourlyCompletion();
        this.triggerAutoScrollEvent();
      }
    });

    // Legacy subscriptions (for backward compatibility)
    this.moveInDateControl.valueChanges.subscribe(() => {
      this.isMoveInDateValid.set(!!(this.moveInDateControl.valid && this.moveInDateControl.value));
      this.onMoveInDateChange();
      this.triggerAutoScrollEvent();
    });

    this.smartMeterTermsControl.valueChanges.subscribe(() => {
      this.isTermsAccepted.set(!!this.smartMeterTermsControl.value);
      this.updateHalfHourlyCompletion();
      this.triggerAutoScrollEvent();
    });

    this.consentDataControl.valueChanges.subscribe((data) => {
      if (this.scopeControl.value === 'HH') {
        if (data) {
          const consent = this.createConsentObject(data);
          this._value = consent;
        } else {
          this._value = null;
        }
        this.updateHalfHourlyCompletion();
      }
    });
  }

  triggerAutoScrollEvent(): void {
    setTimeout(() => {
      this.triggerAutoScroll.emit();
    }, 100);
  }

  // Helper methods for template
  getEffectiveMoveInDate(): Date | null {
    const agreementData = this.smartMeterAgreementControl.value;
    return agreementData?.moveInDate || this.moveInDateControl.value;
  }

  getEffectiveTermsAccepted(): boolean {
    const agreementData = this.smartMeterAgreementControl.value;
    return agreementData?.termsAccepted || !!this.smartMeterTermsControl.value;
  }

  ngAfterViewInit(): void {}

  // Setup file upload subscription when bill upload component becomes available
  private setupBillUploadSubscription(): void { /* handled by child component */ }

  writeValue(value: Consent | null): void {
    this._value = value;
    
    // Update the complete state based on the value
    this.smartConsentComplete.set(!!value);
    
    // If it's an annual checkbox consent, update the control
    if (value?.consentType === ConsentType.CHECKBOX && value.scope === 'ANNUAL' && typeof value.consentData === 'object') {
      this.annualCheckboxConsentControl.setValue(value.consentData as CheckboxConsent, { emitEvent: false });
    }
  }

  registerOnChange(fn: (value: Consent | null) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.scopeControl.disable();
      this.annualCheckboxConsentControl.disable();
      this.smartMeterAgreementControl.disable();
      // Legacy controls
      this.moveInDateControl.disable();
      this.smartMeterTermsControl.disable();
    } else {
      this.scopeControl.enable();
      this.annualCheckboxConsentControl.enable();
      this.smartMeterAgreementControl.enable();
      // Legacy controls
      this.moveInDateControl.enable();
      this.smartMeterTermsControl.enable();
    }
  }

  // Stripe card changes handled inside SmartConsentOptionsComponent via consentDataControl

  // Helper methods for template
  processingConsent(): boolean {
    return this.processingSmartConsent();
  }

  isSmartConsentComplete(): boolean {
    return this.smartConsentComplete();
  }

  // consent method managed in child component

  onGranularityChange(event: any): void {
    const scope = event.value as 'ANNUAL' | 'HH';
    this.scopeControl.setValue(scope);
    
    this.triggerAutoScrollEvent();
  }

  private onMoveInDateChange(): void {
    // Reset consent method section when move-in date changes
    this.resetConsentMethodSection();
    this.updateHalfHourlyCompletion();
  }

  private resetConsentMethodSection(): void {
    // Reset form state
    this._value = null;
  this.smartConsentComplete.set(false);
  this.consentDataControl.reset();
  this._onChange(null);
  }

  private onScopeValueChange(scope: string | null): void {
    // Reset form state when changing scope
    this._value = null;
    this.smartConsentComplete.set(false);
    this._onChange(null);

    // Reset all form controls
    this.annualCheckboxConsentControl.reset();
    this.moveInDateControl.reset();
    this.smartMeterTermsControl.reset();
    
    // Reset reactive signals
    this.isMoveInDateValid.set(false);
    this.isTermsAccepted.set(false);
    
    // Reset consent method section
    this.resetConsentMethodSection();

    // Load smart meter terms when switching to half-hourly (now handled by SmartMeterAgreementComponent)
    if (scope === 'HH') {
      // Terms loading is now handled by SmartMeterAgreementComponent
    } else {
      // Clear terms link when switching away from half-hourly
      this.smartMeterTermsLink.set(null);
    }
  }

  private updateHalfHourlyCompletion(): void {
    if (this.scopeControl.value === 'HH') {
      const consentMethodComplete = !!this.consentDataControl.value;
      const agreementData = this.smartMeterAgreementControl.value;
      
      // Check if agreement is complete (either from new control or legacy controls)
      const agreementComplete = agreementData || (this.isMoveInDateValid() && this.isTermsAccepted());

      if (agreementComplete && consentMethodComplete && this._value) {
        // The _value should already be a proper Consent object from the consent method handlers
        this.smartConsentComplete.set(true);
        this._onChange(this._value);
        this._onTouched();
      } else {
        this.smartConsentComplete.set(false);
        this._onChange(null);
      }
    }
  }

  // Helper methods to check supported consent types
  isMacAddressSupported(): boolean {
    return this.supportedSmartMeterConsentTypes.includes(ConsentType.MAC_ADDRESS);
  }

  isBillUploadSupported(): boolean {
    return this.supportedSmartMeterConsentTypes.includes(ConsentType.BILL_UPLOAD);
  }

  isStripeSupported(): boolean {
    return this.supportedSmartMeterConsentTypes.includes(ConsentType.STRIPE);
  }

  // Helper methods to create standardized Consent objects
  // This ensures the form control always returns a value of Consent type
  private createConsentObject(raw: SmartConsentValue): Consent {
    const scope = this.scopeControl.value as 'HH' | 'ANNUAL' | null;
    if (!scope) throw new Error('Scope must be selected before creating consent');
  const { consentCategory, consentType, consentData } = raw;
  const consent: Consent = { consentCategory, consentType, consentData, scope };
    if (scope === 'HH') {
      consent.moveInDate = this.moveInDateControl.value || undefined;
      consent.termsAccepted = this.smartMeterTermsControl.value || undefined;
    }
    return consent;
  }

  // Create annual consent from checkbox consent data
  private createAnnualConsentFromCheckbox(checkboxConsent: CheckboxConsent): Consent {
    return {
      consentCategory: ConsentCategory.CHECKBOX,
      consentType: ConsentType.CHECKBOX,
      consentData: checkboxConsent,
      scope: 'ANNUAL'
    };
  }


  // Open smart meter terms and conditions in new tab
  openSmartMeterTerms(): void {
    const termsLink = this.smartMeterTermsLink();
    if (termsLink) {
      window.open(termsLink, '_blank');
    } else {
      this.utilService.openSnackBar('Terms and conditions link not available', 'Error', 'error');
    }
  }

  // Stripe logic is now encapsulated within SmartConsentOptionsComponent
  @ViewChild(SmartConsentOptionsComponent)
  private smartConsentOptionsCmp?: SmartConsentOptionsComponent;

  // Expose a passthrough reset for Stripe so parents (e.g., dialog) can trigger a retry
  resetStripe(): void {
    // Delegate to child component if present
    this.smartConsentOptionsCmp?.resetStripe();
    // Also clear current consent value to force re-entry
    this.consentDataControl.reset();
    this._value = null;
    this.smartConsentComplete.set(false);
    this._onChange(null);
  }
}
